import {
  documentChecksum,
  type ArchivedDocument,
  type DocumentArchiveGateway,
} from '@forq/contracts';
import type { CustomersGateway } from '@forq/customers-data';
import { buyerFromCustomer, type Invoice } from '@forq/finance-data';
import type { TenantCompanyProfile } from '@forq/platform-data';
import { renderInvoiceHtml } from './invoiceDocument';

/**
 * Den ausgestellten Beleg archivieren -- der Schritt, der bis 09/2026 fehlte.
 *
 * Ohne ihn wurde jede Rechnung bei jedem Aufruf LIVE neu gerendert, aus dem AKTUELLEN Firmenprofil.
 * Ein geänderter Briefkopf ließ eine zwei Jahre alte Rechnung beim Nachdruck anders aussehen als
 * beim Versand -- § 14b UStG verlangt aber die Aufbewahrung im ursprünglichen Zustand.
 *
 * Archiviert wird im Moment des Festschreibens (Vergabe der RE-/GS-Nummer), weil der Beleg erst
 * dann seine gesetzliche Nummer trägt. Die Ablage ist schreib-einmalig und idempotent, ein
 * Wiederholen nach einem Netzabbruch ist deshalb gefahrlos.
 */

/** Empfängeranschrift aus dem Kundenstamm; ist der Kunde nicht auffindbar, bleibt der Name. */
async function resolveBuyer(
  tenantId: string,
  invoice: Invoice,
  customersGateway: CustomersGateway,
) {
  try {
    const page = await customersGateway.searchCustomers({ tenantId, query: invoice.customerNumber, pageSize: 5 });
    const match = page.items.find((item) => item.number === invoice.customerNumber);
    if (match) return buyerFromCustomer(await customersGateway.getCustomer(tenantId, match.id), invoice);
  } catch { /* Der Beleg entsteht auch ohne Stammtreffer -- mit dem Namen der Rechnung. */ }
  return { name: invoice.customerName, street: '', postalCode: '', city: '', countryCode: 'DE' };
}

/** Der Beleg als druckfertiges HTML, genau so wie ihn die Belegansicht zeigt. */
export async function renderInvoiceForArchive(
  tenantId: string,
  invoice: Invoice,
  company: TenantCompanyProfile,
  customersGateway: CustomersGateway,
): Promise<{ readonly html: string; readonly buyer: Awaited<ReturnType<typeof resolveBuyer>> }> {
  const buyer = await resolveBuyer(tenantId, invoice, customersGateway);
  return { html: renderInvoiceHtml({ invoice, company, buyer }), buyer };
}

/**
 * Legt den festgeschriebenen Beleg ab. Wirft, wenn die Ablage nicht gelingt -- der Aufrufer MUSS
 * das sichtbar machen: eine gestellte Rechnung ohne Archiveintrag ist ein offener Mangel, kein
 * Schönheitsfehler. Ohne gesetzliche Nummer wird nichts archiviert (ein Entwurf ist kein Beleg).
 */
export async function archiveIssuedInvoice(
  tenantId: string,
  invoice: Invoice,
  company: TenantCompanyProfile,
  customersGateway: CustomersGateway,
  archiveGateway: DocumentArchiveGateway,
): Promise<ArchivedDocument | null> {
  const documentNumber = invoice.invoiceNumber;
  if (!documentNumber) return null;

  const { html, buyer } = await renderInvoiceForArchive(tenantId, invoice, company, customersGateway);
  const checksum = await documentChecksum(html);
  return archiveGateway.archiveDocument({
    tenantId,
    kind: invoice.kind === 'credit_note' ? 'credit_note' : 'invoice',
    documentNumber,
    contentHtml: html,
    checksum,
    // Die Eingangsdaten mit ablegen: damit ist im Nachhinein belegbar, WORAUS der Beleg entstand --
    // auch dann noch, wenn Firmenprofil oder Kundenstamm sich längst geändert haben.
    snapshot: { invoice, company, buyer, archivedFrom: 'forq-web' },
  });
}
